import { useCallback, useState } from 'react';
import { Loader2, RefreshCw, AlertTriangle, CheckCircle2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { diagnosticsAPI } from '../../lib/api';
import { toast } from 'sonner';
import { getErrorMessage } from '../../lib/errors';
import { useJobPolling, useJobResume, JobErrorBlock } from './JobPollingPanel';

const MAX_DISPLAY_ROWS = 50;
const APPLY_OPERATION = 'currency_backfill_apply';

export function SmoobuCurrencyBackfillPanel() {
  const [previewLoading, setPreviewLoading] = useState(false);
  const [applyStarting, setApplyStarting] = useState(false);
  const [confirmApply, setConfirmApply] = useState(false);
  const [results, setResults] = useState(null);
  const [applyError, setApplyError] = useState(null);

  // Apply used to trigger a full paginated Smoobu API resync (400-day
  // window, per-booking DB writes) synchronously in-request. It now starts
  // a JobStore background job (services/chunked_chain.py) and this panel
  // polls it via the shared hook -- same pattern as every other converted
  // Diagnostics card, not a reimplementation.
  const applyPoll = useJobPolling({
    fetchStatus: diagnosticsAPI.smoobuBackfillJobStatus,
    onCompleted: useCallback((result) => {
      setResults(result);
      setApplyError(null);
      toast.success(
        `Currency backfill applied: re-synced from Smoobu. ` +
        `${result.remaining_eur_count} bookings still labeled EUR after (expect 0).`
      );
    }, []),
    onFailed: useCallback((error) => { setApplyError(error); toast.error(error); }, []),
  });
  useJobResume({ fetchLatest: () => diagnosticsAPI.smoobuBackfillJobLatest(APPLY_OPERATION), poll: applyPoll });

  const runPreview = async () => {
    setPreviewLoading(true);
    setResults(null);
    setApplyError(null);
    setConfirmApply(false);
    try {
      const { data } = await diagnosticsAPI.smoobuBackfillCurrencyPreview();
      setResults(data);
    } catch (err) {
      setResults({ error: getErrorMessage(err) });
      toast.error(getErrorMessage(err));
    } finally {
      setPreviewLoading(false);
    }
  };

  const runApply = async () => {
    setConfirmApply(false);
    setApplyStarting(true);
    setResults(null);
    setApplyError(null);
    try {
      const { data } = await diagnosticsAPI.smoobuBackfillCurrencyApplyStart();
      if (data.job_id) applyPoll.start(data.job_id);
    } catch (err) {
      setApplyError(getErrorMessage(err));
    } finally {
      setApplyStarting(false);
    }
  };

  const running = previewLoading || applyStarting || applyPoll.polling;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg flex items-center gap-2">
          <RefreshCw className="h-5 w-5 text-blue-500" />
          Smoobu Currency Mislabel Backfill
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-sm text-muted-foreground">
          Every Smoobu reservation was hardcoded currency="EUR" regardless of what Smoobu
          actually reports (verified live: Smoobu has no top-level currency field;
          priceElements[].currencyCode is GBP for every eci booking). Preview shows bookings
          currently mislabeled EUR by property; Apply re-syncs from the Smoobu API with the
          corrected parser to re-stamp them. This only corrects the currency label -- revenue
          amounts are untouched, they were already correct GBP figures.
        </p>

        <div className="flex gap-2">
          <Button onClick={runPreview} disabled={running} data-testid="smoobu-currency-backfill-preview">
            {running ? (
              <Loader2 className="h-4 w-4 animate-spin mr-2" />
            ) : (
              <RefreshCw className="h-4 w-4 mr-2" />
            )}
            Preview
          </Button>

          {!confirmApply ? (
            <Button
              variant="destructive"
              onClick={() => setConfirmApply(true)}
              disabled={running}
              data-testid="smoobu-currency-backfill-apply"
            >
              Apply
            </Button>
          ) : (
            <Button
              variant="destructive"
              onClick={runApply}
              disabled={running}
              data-testid="smoobu-currency-backfill-confirm-apply"
            >
              {running ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : (
                <AlertTriangle className="h-4 w-4 mr-2" />
              )}
              Confirm Apply
            </Button>
          )}
        </div>

        <JobErrorBlock error={applyError} testId="smoobu-currency-backfill-apply-error" />

        {results && (
          <div className="space-y-4" data-testid="smoobu-currency-backfill-result">
            {results.error && (
              <div className="text-sm text-red-600 dark:text-red-400 border border-red-200 dark:border-red-800 rounded-lg p-3">
                Error: {results.error}
              </div>
            )}

            {/* Dry-run results */}
            {results.dry_run && !results.error && (
              <>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  <div className="rounded-lg border p-3">
                    <p className="text-xs text-muted-foreground">Labeled EUR</p>
                    <p className="text-xl font-bold tabular-nums">{results.mislabeled_count}</p>
                  </div>
                  <div className="rounded-lg border p-3">
                    <p className="text-xs text-muted-foreground">Properties affected</p>
                    <p className="text-xl font-bold tabular-nums">{results.by_property?.length || 0}</p>
                  </div>
                  <div className="rounded-lg border p-3">
                    <p className="text-xs text-muted-foreground">Mode</p>
                    <Badge variant="secondary">Preview</Badge>
                  </div>
                </div>

                <p className="text-sm text-muted-foreground">{results.message}</p>

                {results.by_property?.length > 0 && (
                  <div className="space-y-1">
                    <p className="text-sm font-medium">
                      {results.by_property.length > MAX_DISPLAY_ROWS
                        ? `Showing ${MAX_DISPLAY_ROWS} of ${results.by_property.length} properties`
                        : `${results.by_property.length} propert${results.by_property.length !== 1 ? 'ies' : 'y'}`}
                    </p>
                    <div className="overflow-x-auto border rounded-lg">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="border-b bg-muted/50">
                            <th className="text-left px-3 py-2 font-medium">Property</th>
                            <th className="text-right px-3 py-2 font-medium">Mislabeled</th>
                            <th className="text-left px-3 py-2 font-medium">Registered currency</th>
                          </tr>
                        </thead>
                        <tbody>
                          {results.by_property.slice(0, MAX_DISPLAY_ROWS).map((r) => (
                            <tr key={r.property_id} className="border-b last:border-0">
                              <td className="px-3 py-2 text-xs">{r.property_name}</td>
                              <td className="px-3 py-2 text-right tabular-nums">{r.mislabeled_count}</td>
                              <td className="px-3 py-2">
                                <Badge variant="outline" className="text-[10px]">
                                  {r.property_registered_currency || '—'}
                                </Badge>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </>
            )}

            {/* Apply results */}
            {results.dry_run === false && !results.error && (
              <>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  <div className="rounded-lg border p-3">
                    <p className="text-xs text-muted-foreground">Mislabeled before</p>
                    <p className="text-xl font-bold tabular-nums">{results.pre_existing_mislabeled_count}</p>
                  </div>
                  <div className="rounded-lg border p-3">
                    <p className="text-xs text-muted-foreground">Still EUR after</p>
                    <p className={`text-xl font-bold tabular-nums ${results.remaining_eur_count > 0 ? 'text-amber-600 dark:text-amber-400' : ''}`}>
                      {results.remaining_eur_count}
                    </p>
                  </div>
                  <div className="rounded-lg border p-3">
                    <p className="text-xs text-muted-foreground">GBP after</p>
                    <p className="text-xl font-bold tabular-nums">{results.gbp_count_after}</p>
                  </div>
                </div>

                {results.sync_result && (
                  <div className="text-sm text-muted-foreground">
                    Sync: {results.sync_result.synced} fetched,{' '}
                    {results.sync_result.created} created,{' '}
                    {results.sync_result.updated} updated
                  </div>
                )}

                <p className="text-sm text-muted-foreground">{results.message}</p>

                {results.remaining_eur_count === 0 ? (
                  <div className="flex items-center gap-2 text-sm text-green-600 dark:text-green-400">
                    <CheckCircle2 className="h-4 w-4" />
                    Currency backfill complete, no EUR-labeled bookings remain.
                  </div>
                ) : (
                  <div className="flex items-center gap-2 text-sm text-amber-600 dark:text-amber-400">
                    <AlertTriangle className="h-4 w-4" />
                    {results.remaining_eur_count} booking(s) still labeled EUR -- investigate before assuming this is done.
                  </div>
                )}
              </>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
